import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/utils/axios";
import cls from "./AdminFriendRequestsMonitor.module.scss";
import avatarPng from "@/assets/UI/avatar.png";

const STATUS_OPTIONS = [
  "all",
  "pending",
  "accepted",
  "declined",
  "cancelled",
  "expired",
];

const statusClassName = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "accepted") return cls.statusAccepted;
  if (s === "declined") return cls.statusDeclined;
  if (s === "cancelled" || s === "expired") return cls.statusMuted;
  return cls.statusPending;
};

const formatStatusLabel = (status) => {
  const s = String(status || "").toLowerCase();
  if (!s) return "Unknown";
  return s.charAt(0).toUpperCase() + s.slice(1);
};

function ProfileBlock({ user, alt }) {
  const avatarSrc = user?.avatar?.url || user?.avatar?.key || avatarPng;

  return (
    <div className={cls.profileCard}>
      <div className={cls.avatarWrap}>
        <img src={avatarSrc} alt={alt} className={cls.avatar} />
      </div>

      <div className={cls.profileText}>
        <strong>{user?.username || "Unknown"}</strong>
        <span>
          {user?.country || "—"} · {user?.city || "—"}
        </span>
      </div>
    </div>
  );
}

export default function AdminFriendRequestsMonitor() {
     const PAGE_SIZE = 50;
  const [status, setStatus] = useState("all");
   const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyMap, setBusyMap] = useState({});
  const [confirmMap, setConfirmMap] = useState({});

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const q = new URLSearchParams();
     q.set("page", String(page));
      q.set("limit", String(PAGE_SIZE));
      if (status && status !== "all") q.set("status", status);

      const res = await api.get(`/v1/admin/friend-requests?${q.toString()}`);
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setMeta({
        totalPages: res.data?.totalPages || 1,
        hasNextPage: !!res.data?.hasNextPage,
        hasPrevPage: !!res.data?.hasPrevPage,
        total: Number(res.data?.total || 0),
      });
    } catch (err) {
      console.error("Failed to load admin friend requests", err);
      setError(
        err?.response?.data?.message || err?.message || "Failed to load",
      );
      setItems([]);
       setMeta({
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
}, [page, status]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

   useEffect(() => {
    setPage(1);
  }, [status]);

  const pendingCount = useMemo(
    () => items.filter((item) => item.status === "pending").length,
    [items],
  );

  const acceptedCount = useMemo(
    () => items.filter((item) => item.status === "accepted").length,
    [items],
  );

  const declinedCount = useMemo(
    () => items.filter((item) => item.status === "declined").length,
    [items],
  );

  const handleAskAccept = useCallback((requestId) => {
    setConfirmMap((prev) => ({ ...prev, [requestId]: true }));
  }, []);

  const handleCloseConfirm = useCallback((requestId) => {
    setConfirmMap((prev) => {
      const next = { ...prev };
      delete next[requestId];
      return next;
    });
  }, []);

  const handleAccept = useCallback(
    async (requestId, connectionType) => {
      if (!requestId) return;

      setBusyMap((prev) => ({ ...prev, [requestId]: "accept" }));

      try {
        await api.post(`/v1/admin/friend-requests/${requestId}/accept`, {
          connectionType,
        });

        setConfirmMap((prev) => {
          const next = { ...prev };
          delete next[requestId];
          return next;
        });

        await fetchRows();
      } catch (err) {
        console.error("Failed to accept friend request from admin", err);
      } finally {
        setBusyMap((prev) => {
          const next = { ...prev };
          delete next[requestId];
          return next;
        });
      }
    },
    [fetchRows],
  );

  const handleDecline = useCallback(
    async (requestId) => {
      if (!requestId) return;

      setBusyMap((prev) => ({ ...prev, [requestId]: "decline" }));

      try {
        await api.post(`/v1/admin/friend-requests/${requestId}/decline`);
        await fetchRows();
      } catch (err) {
        console.error("Failed to decline friend request from admin", err);
      } finally {
        setBusyMap((prev) => {
          const next = { ...prev };
          delete next[requestId];
          return next;
        });
      }
    },
    [fetchRows],
  );

  return (
    <div className={cls.wrap}>
      <header className={cls.hero}>
        <div className={cls.heroText}>
          <span className={cls.eyebrow}>Admin request control</span>
          <h1 className={cls.title}>Friend Request Monitoring</h1>
          <p className={cls.sub}>
            Review incoming connection requests, check sender and receiver
            details, and take action on behalf of receiver users.
          </p>
        </div>

        <div className={cls.heroStats}>
          <div className={cls.statCard}>
            <span className={cls.statLabel}>Total</span>
             <strong>{meta.total}</strong>
          </div>
          <div className={cls.statCard}>
            <span className={cls.statLabel}>Pending</span>
            <strong>{pendingCount}</strong>
          </div>
          <div className={cls.statCard}>
            <span className={cls.statLabel}>Accepted</span>
            <strong>{acceptedCount}</strong>
          </div>
          <div className={cls.statCard}>
            <span className={cls.statLabel}>Declined</span>
            <strong>{declinedCount}</strong>
          </div>
        </div>
      </header>

      <section className={cls.toolbar}>
        <div className={cls.filters}>
          <label className={cls.filterControl}>
            <span>Filter by status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {formatStatusLabel(option)}
                </option>
              ))}
            </select>
          </label>

          <div className={cls.pendingBadge}>
            <span className={cls.pendingDot} />
            Pending Requests: {pendingCount}
          </div>
        </div>
      </section>

      {error ? <div className={cls.error}>{error}</div> : null}

      <section className={cls.tableWrap} aria-busy={loading ? "true" : "false"}>
        <table className={cls.table}>
          <thead>
            <tr>
              <th>Sender</th>
              <th>Receiver</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              [...Array(8)].map((_, i) => (
                <tr key={`sk-${i}`} className={cls.skelRow}>
                  <td colSpan={5}>
                    <div className={cls.skel} />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className={cls.empty}>
                  No friend requests found.
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const from = item.fromUserProfile || {};
                const to = item.toUserProfile || {};
                const busyState = busyMap[item.id];
                const isPending = item.status === "pending";

                return (
                  <tr key={item.id}>
                    <td>
                      <ProfileBlock user={from} alt="sender avatar" />
                    </td>

                    <td>
                      <ProfileBlock user={to} alt="receiver avatar" />
                    </td>

                    <td>
                      <span
                        className={`${cls.statusPill} ${statusClassName(item.status)}`}
                      >
                        {formatStatusLabel(item.status)}
                      </span>
                      <span className={cls.connectionLabel}>
                        <strong>{item.connectionType || "—"}</strong>
                      </span>
                    </td>

                    <td>
                      <div className={cls.actions}>
                        <button
                          type="button"
                          className={cls.acceptBtn}
                          onClick={() => handleAskAccept(item.id)}
                          disabled={!isPending || !!busyState}
                        >
                          {busyState === "accept" ? "Accepting..." : "Accept"}
                        </button>

                        <button
                          type="button"
                          className={cls.declineBtn}
                          onClick={() => handleDecline(item.id)}
                          disabled={!isPending || !!busyState}
                        >
                          {busyState === "decline" ? "Declining..." : "Decline"}
                        </button>
                      </div>

                      {confirmMap[item.id] ? (
                        <div className={cls.confirmBox}>
                          <p>Does the receiver personally know the sender?</p>

                          <div className={cls.confirmActions}>
                            <button
                              type="button"
                              onClick={() =>
                                handleAccept(item.id, "know_personally")
                              }
                              disabled={!!busyState}
                            >
                              Yes, personally know
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                handleAccept(item.id, "online_only")
                              }
                              disabled={!!busyState}
                            >
                              No, online only
                            </button>

                            <button
                              type="button"
                              className={cls.cancelBtn}
                              onClick={() => handleCloseConfirm(item.id)}
                              disabled={!!busyState}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </section>
      <div className={cls.pagination}>
        <button
          type="button"
          className={cls.pageBtn}
          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          disabled={loading || !meta.hasPrevPage}
        >
          Previous
        </button>

        <span className={cls.pageInfo}>
          Page {page} / {meta.totalPages || 1}
        </span>

        <button
          type="button"
          className={cls.pageBtn}
          onClick={() => setPage((prev) => prev + 1)}
          disabled={loading || !meta.hasNextPage}
        >
          Next
        </button>
      </div>
    </div>
  );
}
